import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ExportModal } from '@components/ExportModal';
import { AsociadosTable } from '@components/AsociadosTable';
import { ColumnMappingModal } from '@components/ColumnMappingModal';
import { EditAsociadoModal } from '@components/EditAsociadoModal';
import { EliminarLegajoModal } from '@components/EliminarLegajoModal';
import { EliminarPadronModal } from '@components/EliminarPadronModal';
import { SepararInconsistenciasModal } from '@components/SepararInconsistenciasModal';
import { UploadDropzone } from '@components/UploadDropzone';
import { HubspotSyncCard } from '@components/admin/HubspotSyncCard';
import { PadronPisosModal } from '@components/PadronPisosModal';
import { EstadoLegajoBadge } from '@components/EstadoLegajoBadge';
import {
  cerrarPadron,
  confirmCorrecciones,
  deleteAsociado,
  downloadInconsistenciasBlob,
  eliminarPadron,
  previewCierrePadron,
  previewPadron,
  rechazarLegajo,
  revalidarPadron,
  separarInconsistencias,
  updateAsociado,
  validarLegajo,
  validarPadron,
  type ValidarPadronResult,
  type CorreccionesResult,
  type EliminarPadronResult,
  type Legajo,
  type PreviewCierre,
  type PreviewResponse,
} from '@lib/services/padrones.service';
import { useEliminarLegajo } from './hooks/useEliminarLegajo';
import { usePadronData, type Tab as TabPadron } from './hooks/usePadronData';
import { useRenaperJobEndInvalidation } from './hooks/useRenaperJobEndInvalidation';
import {
  getResumenRenaper,
  getRenaperBatchJobLatest,
  validarRenaperPadron,
} from '@lib/services/renaper.service';
import { useAuthStore } from '@lib/store/auth.store';
import { planDeCarga, promotorDeCarga } from '@lib/empresas/plan-de-carga';
import { UserRole, isMedicus, type Asociado, type User } from '@lib/types';
import { useCanMutate } from '@lib/hooks/useCanMutate';
import { puedeVer } from '@lib/permissions/catalog';
import { InfoAlert } from '@components/medicus-ui/InfoAlert';
import { RefetchBar, Spinner, TableSkeleton } from '@components/medicus-ui/Loaders';
import { ErrorInline } from '@components/ErrorInline';
import { AlertCircle, Info } from 'lucide-react';

// El tipo vive en el hook (que también lo consume) para no duplicarlo.
type Tab = TabPadron;

const TAB_LABEL: Record<Tab, string> = {
  errores: 'Errores',
  advertencias: 'Advertencias',
  todos: 'Todos los registros',
  legajos: 'Legajos',
};

const ESTADO_PADRON_UI: Record<string, { label: string; cls: string }> = {
  borrador: { label: 'Borrador', cls: 'status status-warning' },
  pendiente_validacion: { label: 'Pendiente de validación', cls: 'status status-warning' },
  cerrado: { label: 'Cerrado', cls: 'status status-info' },
};

// El estado del legajo se pinta con <EstadoLegajoBadge> (catálogo único en
// lib/estado-legajo.ts). El de PADRÓN sigue con su propio mapa (ESTADO_PADRON_UI).

function loadOrErrorView(q: { data?: unknown; isLoading: boolean; isError: boolean }) {
  if (!q.data && q.isLoading) {
    return (
      <p
        className="text-element-mid"
        style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
      >
        <Spinner /> Cargando padrón…
      </p>
    );
  }
  if (q.isError || !q.data) {
    return <p className="status status-error">No se pudo cargar el padrón.</p>;
  }
  return null;
}

function labelCerrarBoton(estado: string, isPending: boolean): string {
  if (estado === 'cerrado') return 'Padrón cerrado';
  if (estado === 'pendiente_validacion') return 'Esperando validación Medicus';
  if (isPending) return 'Cerrando…';
  return 'Cerrar padrón';
}

function extractServerError(err: unknown): string {
  const e = err as {
    response?: {
      data?: { error?: { message?: string; details?: Array<{ message: string }> } };
    };
  };
  const data = e.response?.data?.error;
  let msg = data?.message ?? 'Error al procesar el archivo';
  if (data?.details && data.details.length > 0) {
    msg += ' — ' + data.details.map((d) => d.message).join(', ');
  }
  return msg;
}

/**
 * Indexa los legajos por su número de legajo y devuelve el estado actual,
 * para que la tabla de asociados pueda pintar el chip de estado al lado
 * del titular. Solo tiene sentido cuando el padrón ya pasó del borrador
 * (de lo contrario los legajos todavía no existen).
 *
 * Extraída fuera del componente para mantener la complejidad cognitiva
 * de PadronDetallePage bajo el umbral de Sonar S3776.
 */
function buildEstadosLegajoMap(
  padronCerrado: boolean,
  padronEnValidacion: boolean,
  legajos: Legajo[] | undefined,
): Map<string, string> | undefined {
  if (!legajos) return undefined;
  if (!padronCerrado && !padronEnValidacion) return undefined;
  return new Map(legajos.map((l) => [l.numero, l.estado]));
}

/**
 * Agrupa las observaciones que el titular reportó en cada legajo, indexadas
 * por asociadoId. Permite que la tabla muestre el ícono de bandera amarilla
 * sin tener que recorrer todos los legajos en cada render.
 */
function groupObservacionesByAsociadoId(
  legajos: Legajo[] | undefined,
): Map<string, NonNullable<Legajo['observaciones']>> | undefined {
  if (!legajos) return undefined;
  const m = new Map<string, NonNullable<Legajo['observaciones']>>();
  for (const l of legajos) {
    for (const o of l.observaciones ?? []) {
      const arr = m.get(o.asociadoId) ?? [];
      arr.push(o);
      m.set(o.asociadoId, arr);
    }
  }
  return m;
}

/**
 * Handler del botón Eliminar de cada fila — confirm nativo simple y luego
 * dispara la mutation. Extraído fuera del componente para que no cuente
 * en la complejidad cognitiva del page.
 */
function confirmarYEliminarAsociado(
  a: Asociado,
  mutate: (id: string) => void,
  padronCerrado = false,
): void {
  const nombre = `${a.nombres ?? ''} ${a.apellSolt ?? ''}`.trim() || `fila ${a.rowNumber}`;
  // Después del cierre la fila ya tiene legajo: quitarla también la saca de él
  // (y si era el único adicional de un legajo bis, el bis se borra).
  const aviso = padronCerrado
    ? ` El padrón ya está cerrado: también se la quita del legajo ${a.legajo ?? ''}.`.trimEnd()
    : '';
  if (window.confirm(`¿Eliminar a ${nombre} del padrón?${aviso}`)) {
    mutate(a.id);
  }
}

/**
 * Calcula todas las flags y datos derivados de la página de detalle del padrón.
 * Vive afuera de `PadronDetallePage` para que la cantidad de boolean operators
 * (||, &&, ternario, ??) que mira `user.rol`, `padron.estado`, `tab`, etc. no
 * cuente en la complejidad cognitiva del componente (Sonar S3776, umbral 15).
 */
function computePageDerivations(input: {
  padron: NonNullable<ReturnType<typeof usePadronData>['padron']['data']>;
  user: User | null;
  esAdminMedicus: boolean;
  /** true para los 3 roles Medicus (incluye USER_MEDICUS de solo lectura). */
  esMedicusVisible: boolean;
  /**
   * `false` para el rol Medicus de solo lectura (USER_MEDICUS). Cuando es
   * `false`, ocultamos todos los botones que mutan estado.
   */
  puedeMutar: boolean;
  padronEnValidacion: boolean;
  tab: Tab;
  legajosData: Legajo[] | undefined;
}) {
  const { padron: p, user, esAdminMedicus, esMedicusVisible, puedeMutar, padronEnValidacion, tab, legajosData } = input;
  const puedeCerrar = p.registrosError === 0 && p.estado === 'borrador';
  // Quitar una fila: en borrador, quien puede mutar. Después del cierre
  // (pendiente de validación) solo un admin Medicus con la clave de ACCIÓN
  // `view:padron-quitar-fila` (VEN-636) — el server la vuelve a exigir y además
  // desvincula la fila del legajo. Una vez `cerrado`, nadie.
  const mostrarEliminarFila =
    puedeMutar &&
    (p.estado === 'borrador' ||
      (padronEnValidacion &&
        esAdminMedicus &&
        !!user &&
        puedeVer(user, 'view:padron-quitar-fila')));
  // Descarga de inconsistencias: cualquier estado mientras haya errores
  // (UE puede conciliar contra la fuente original también post-cierre).
  const mostrarBotonInconsistencias = p.registrosError > 0;
  // Separar inconsistencias: solo en borrador (mueve filas entre padrones).
  const mostrarBotonSeparar = puedeMutar && p.registrosError > 0 && p.estado === 'borrador';
  const mostrarBotonRevalidar = puedeMutar && p.estado === 'borrador';
  // Eliminar: SuperAdmin Medicus en cualquier estado; UE solo en borrador;
  // AdminMedicus y USER_MEDICUS no pueden.
  const mostrarBotonEliminar =
    puedeMutar &&
    (user?.rol === UserRole.SUPERADMIN_MEDICUS ||
      (user?.rol === UserRole.USUARIO_EMPRESA && p.estado === 'borrador'));
  const mostrarBotonDescargarPadron = true;
  // Emails titulares: cualquier Medicus (incluido solo lectura), fuera de borrador.
  // Emails titulares: cualquier Medicus (incluye USER_MEDICUS, rol de solo
  // lectura). La descarga es informativa, no muta estado.
  const mostrarBotonDescargarEmails = esMedicusVisible && p.estado !== 'borrador';
  const mostrarTablaAsociados =
    tab === 'todos' || tab === 'errores' || tab === 'advertencias';
  const pendientes =
    legajosData?.filter((l) => l.estado === 'pendiente_validacion_medicus') ?? [];
  // Un admin Medicus siempre puede finalizar un padrón en `pendiente_validacion`,
  // incluso si NO quedan legajos por validar — p.ej. padrones cuyo único
  // contenido es un `legajo_bis` (familiar sin titular cuyo número ya existe) o
  // que quedaron sin legajos. Antes el `pendientes.length > 0` escondía el botón
  // y esos padrones quedaban TRABADOS en `pendiente_validacion` para siempre.
  // `validarPadron` con 0 pendientes solo pasa el padrón a `cerrado`; los
  // `legajo_bis` se resuelven aparte desde la vista Legajos bis (y ahí su gente
  // se suma al grupo del titular ya dado de alta).
  const mostrarBotonValidarBulk = puedeMutar && esAdminMedicus && padronEnValidacion;
  const estadoUi = ESTADO_PADRON_UI[p.estado] ?? {
    label: p.estado,
    cls: 'status status-warning',
  };
  return {
    puedeCerrar,
    mostrarEliminarFila,
    mostrarBotonInconsistencias,
    mostrarBotonSeparar,
    mostrarBotonRevalidar,
    mostrarBotonEliminar,
    mostrarBotonDescargarPadron,
    mostrarBotonDescargarEmails,
    mostrarTablaAsociados,
    pendientes,
    mostrarBotonValidarBulk,
    estadoUi,
  };
}

/**
 * Pestañas del detalle. La de advertencias solo se ofrece si el padrón tiene
 * alguna: en la mayoría es 0 y no aporta.
 */
function tabsVisibles(cantidadAdvertencias: number): Tab[] {
  const conAdvertencias: Tab[] = cantidadAdvertencias > 0 ? ['advertencias'] : [];
  return ['errores', ...conAdvertencias, 'todos', 'legajos'];
}

/**
 * Explica qué son las advertencias y que no frenan nada. Antes solo se veía el
 * número en un KPI y no había forma de saber ni qué eran ni si bloqueaban.
 */
function AdvertenciasBanner({ cantidad, onVer }: { cantidad: number; onVer: () => void }) {
  if (cantidad <= 0) return null;
  const titulo =
    cantidad === 1 ? '1 registro con advertencia' : `${cantidad} registros con advertencia`;
  return (
    <div
      className="card"
      style={{
        marginTop: 12,
        borderLeft: '3px solid #B45309',
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 10,
        justifyContent: 'space-between',
      }}
    >
      <p style={{ margin: 0, maxWidth: '70ch' }}>
        <strong>{titulo}.</strong> Son personas cuyo número de legajo ya existe: se van a
        sumar a ese legajo como integrantes, por eso el archivo no trae al titular.{' '}
        <strong>No bloquean la validación</strong> y no hay nada que corregir.
      </p>
      <button type="button" className="btn btn-secondary btn--sm" onClick={onVer}>
        <span className="btn-font-style">Ver cuáles son</span>
      </button>
    </div>
  );
}

export function PadronDetallePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  // Default 'todos': al entrar al detalle vemos toda la lista; los registros
  // con error van arriba (orden definido en el server: estado=error primero,
  // después rowNumber asc) para que se atiendan primero.
  const [tab, setTab] = useState<Tab>('todos');
  const [editing, setEditing] = useState<Asociado | null>(null);
  const [search, setSearch] = useState('');
  const [eliminarOpen, setEliminarOpen] = useState(false);
  const [separarOpen, setSepararOpen] = useState(false);
  // Modal con la lista de asociados que tuvieron pisos por Renaper. Lo abre
  // el botón "Ver detalle de pisos" del banner del job (admin Medicus).
  const [pisosModalOpen, setPisosModalOpen] = useState(false);
  const { user, empresas } = useAuthStore();
  const [corrPreview, setCorrPreview] = useState<PreviewResponse | null>(null);
  const [corrUploadError, setCorrUploadError] = useState<string | null>(null);
  const [corrConfirmError, setCorrConfirmError] = useState<string | null>(null);
  const [corrResult, setCorrResult] = useState<CorreccionesResult | null>(null);
  const [rechazarTarget, setRechazarTarget] = useState<Legajo | null>(null);
  const [motivoRechazo, setMotivoRechazo] = useState('');
  const elim = useEliminarLegajo(id);

  // `esAdminMedicus`: ADMIN + SUPERADMIN. Habilita acciones de mutación
  // específicas de Medicus (validar/rechazar/Renaper/HubSpot/eliminar).
  // NO incluye USER_MEDICUS (rol de solo lectura).
  const esAdminMedicus =
    user?.rol === UserRole.ADMIN_MEDICUS || user?.rol === UserRole.SUPERADMIN_MEDICUS;
  // `esMedicusVisible`: cualquier Medicus (incluye USER_MEDICUS). Sirve para
  // condicionar paneles de información (banners de Renaper, card de HubSpot
  // sync, descarga de emails de titulares) que USER_MEDICUS también ve.
  const esMedicusVisible = isMedicus(user?.rol ?? UserRole.USUARIO_EMPRESA);
  const puedeMutar = useCanMutate();

  // Queries del padrón encapsulados en un hook para sacar las condiciones
  // `enabled: a && b` del cuerpo de la página (Sonar S3776).
  const { padron, asociados, resumenLegajos, legajos, padronCerrado, padronEnValidacion } =
    usePadronData({ id, tab, search });

  const update = useMutation({
    mutationFn: ({ aId, patch }: { aId: string; patch: Partial<Asociado> }) =>
      updateAsociado(aId, patch),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['padron', id] });
      queryClient.invalidateQueries({ queryKey: ['inconsistencias', id] });
      queryClient.invalidateQueries({ queryKey: ['asociados', id] });
      queryClient.invalidateQueries({ queryKey: ['padrones'] });
      // Los KPIs de la pestaña Legajos salen de acá; sin esto quedaban viejos.
      queryClient.invalidateQueries({ queryKey: ['resumen-padron', id] });
    },
  });

  const eliminarAsociadoMut = useMutation({
    mutationFn: (aId: string) => deleteAsociado(aId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['padron', id] });
      queryClient.invalidateQueries({ queryKey: ['inconsistencias', id] });
      queryClient.invalidateQueries({ queryKey: ['asociados', id] });
      queryClient.invalidateQueries({ queryKey: ['padrones'] });
      // Los KPIs de la pestaña Legajos salen de acá; sin esto quedaban viejos.
      queryClient.invalidateQueries({ queryKey: ['resumen-padron', id] });
    },
  });

  // Descargas: useMutation en vez de useState+try/finally para mantener la
  // complejidad cognitiva del componente bajo el umbral de Sonar (S3776).
  // `isPending` reemplaza al flag de loading; `mutate()` al handler.
  const descargarInc = useMutation({ mutationFn: () => downloadInconsistenciasBlob(id) });
  const [exportar, setExportar] = useState<{ tipo: string; titulo: string } | null>(null);

  // Cerrar padrón con flujo de 2 pasos: primero corremos el preview para
  // detectar merges/bis. Si hay merges con legajos existentes (mismo
  // número en estado pendiente de onboarding), abrimos el modal de
  // confirmación. Si no hay nada que confirmar, cerramos directo.
  const [previewCierre, setPreviewCierre] = useState<PreviewCierre | null>(null);
  const cerrarMut = useMutation({
    mutationFn: (opts: { confirmarMerges?: boolean }) => cerrarPadron(id, opts),
    onSuccess: () => {
      setPreviewCierre(null);
      queryClient.invalidateQueries({ queryKey: ['padron', id] });
      queryClient.invalidateQueries({ queryKey: ['padrones'] });
      // El cierre es lo que CREA los legajos y cambia el estado de cada
      // asociado: sin invalidar esto, la pestaña Legajos aparecía vacía y los
      // contadores viejos justo después de cerrar, hasta un F5.
      queryClient.invalidateQueries({ queryKey: ['legajos', id] });
      queryClient.invalidateQueries({ queryKey: ['resumen-padron', id] });
      queryClient.invalidateQueries({ queryKey: ['asociados', id] });
    },
  });
  const previewCierreMut = useMutation({
    mutationFn: () => previewCierrePadron(id),
    onSuccess: (preview) => {
      if (preview.totales.legajosAMergear === 0 && preview.totales.legajosBis === 0) {
        // Nada que confirmar — cerramos directo.
        cerrarMut.mutate({});
        return;
      }
      setPreviewCierre(preview);
    },
  });
  // Wrapper mínimo para mantener el shape `cerrar.mutate()` que ya usa el
  // botón sin tocar todos los call-sites.
  const cerrar = {
    mutate: () => previewCierreMut.mutate(),
    isPending: previewCierreMut.isPending || cerrarMut.isPending,
  };

  const invalidarTrasValidacion = () => {
    queryClient.invalidateQueries({ queryKey: ['padron', id] });
    queryClient.invalidateQueries({ queryKey: ['legajos', id] });
    queryClient.invalidateQueries({ queryKey: ['padrones'] });
    queryClient.invalidateQueries({ queryKey: ['resumen-padron', id] });
  };

  const validarPadronMut = useMutation({
    mutationFn: () => validarPadron(id),
    onSuccess: invalidarTrasValidacion,
  });

  const validarLegajoMut = useMutation({
    mutationFn: (legajoId: string) => validarLegajo(legajoId),
    onSuccess: invalidarTrasValidacion,
  });

  const rechazarLegajoMut = useMutation({
    mutationFn: ({ legajoId, motivo }: { legajoId: string; motivo: string }) =>
      rechazarLegajo(legajoId, motivo),
    onSuccess: () => {
      invalidarTrasValidacion();
      setRechazarTarget(null);
      setMotivoRechazo('');
    },
  });

  // --- Renaper: resumen + job en background + validación masiva/individual ---
  // El POST de validar padrón ahora dispara un job async y devuelve 202
  // inmediato. Mientras `latestJob.estado === 'corriendo'`, polleamos tanto el
  // job (para saber cuándo termina) como el resumen (para ver progreso en
  // vivo). Al terminar, paramos el polling.
  const renaperLatestJob = useQuery({
    queryKey: ['renaper-latest-job', id],
    queryFn: () => getRenaperBatchJobLatest(id),
    enabled: esMedicusVisible,
    retry: false,
    refetchInterval: (q) => (q.state.data?.estado === 'corriendo' ? 8000 : false),
  });
  const renaperEnCurso = renaperLatestJob.data?.estado === 'corriendo';

  const renaperResumen = useQuery({
    queryKey: ['renaper-resumen', id],
    queryFn: () => getResumenRenaper(id),
    enabled: esMedicusVisible,
    retry: false,
    // Mientras el job corre, refrescamos el resumen junto con el job para
    // mostrar progreso en vivo en el banner.
    refetchInterval: renaperEnCurso ? 8000 : false,
  });
  const invalidarRenaper = () => {
    queryClient.invalidateQueries({ queryKey: ['renaper-resumen', id] });
    queryClient.invalidateQueries({ queryKey: ['renaper-latest-job', id] });
    queryClient.invalidateQueries({ queryKey: ['asociados', id] });
  };
  const validarRenaperPadronMut = useMutation({
    mutationFn: () => validarRenaperPadron(id),
    onSuccess: invalidarRenaper,
  });

  // Cuando el job pasa de `corriendo` a `terminado` o `error`, el polling
  // del resumen ya cortó (refetchInterval evalúa false). Invalidamos manualmente
  // el resumen + asociados para que los chips de arriba y la tabla queden
  // sincronizados con el resumen final del job.
  useRenaperJobEndInvalidation(renaperLatestJob.data?.estado, invalidarRenaper);

  // --- Subir archivo de correcciones (padrones en borrador) ---

  const corrPreviewMutation = useMutation({
    // Correcciones: la organización es la del padrón (header del selector global).
    mutationFn: (file: File) => previewPadron(file),
    onSuccess: (data) => {
      setCorrUploadError(null);
      setCorrConfirmError(null);
      setCorrResult(null);
      setCorrPreview(data);
    },
    onError: (err: unknown) => setCorrUploadError(extractServerError(err)),
  });

  const corrConfirmMutation = useMutation({
    mutationFn: (mapping: Record<string, number>) =>
      // Default true: el archivo de correcciones pisa también las filas que ya
      // estaban OK, no solo las que tenían error. Criterio de producto para que
      // RRHH pueda actualizar datos de un asociado válido sin tener que
      // eliminarlo manualmente primero.
      confirmCorrecciones(id, corrPreview!.uploadToken, mapping, true),
    onSuccess: (result) => {
      setCorrPreview(null);
      setCorrConfirmError(null);
      setCorrResult(result);
      queryClient.invalidateQueries({ queryKey: ['padron', id] });
      queryClient.invalidateQueries({ queryKey: ['inconsistencias', id] });
      queryClient.invalidateQueries({ queryKey: ['asociados', id] });
      queryClient.invalidateQueries({ queryKey: ['padrones'] });
      // Los KPIs de la pestaña Legajos salen de acá; sin esto quedaban viejos.
      queryClient.invalidateQueries({ queryKey: ['resumen-padron', id] });
    },
    onError: (err: unknown) => setCorrConfirmError(extractServerError(err)),
  });

  // Eliminar padrón cerrado (solo Super Admin)
  const eliminarMutation = useMutation<EliminarPadronResult, unknown, void>({
    mutationFn: () => eliminarPadron(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['padrones'] });
      setEliminarOpen(false);
      navigate('/padrones');
    },
  });

  // Separar inconsistencias en un padrón nuevo (sólo en borrador, sólo si hay
  // errores). El backend mueve los legajos completos y deja el original con
  // solo OK. Al terminar llevamos al user a la lista de padrones — el nuevo
  // aparece arriba ("Inconsistencias — …") y el original con stats actualizadas.
  const separarMutation = useMutation({
    mutationFn: () => separarInconsistencias(id),
    onSuccess: () => {
      // Invalidar TODO lo asociado al padrón actual y a la lista global. Tras
      // navegar a /padrones, esta página se desmonta y el queryClient deja
      // los datos viejos para reusar si el user vuelve — los limpiamos para
      // que un eventual re-mount fetchee fresh.
      queryClient.invalidateQueries({ queryKey: ['padrones'] });
      queryClient.invalidateQueries({ queryKey: ['padron', id] });
      queryClient.invalidateQueries({ queryKey: ['asociados', id] });
      queryClient.invalidateQueries({ queryKey: ['inconsistencias', id] });
      setSepararOpen(false);
      navigate('/padrones');
    },
  });

  // Revalidar: re-corre las reglas vigentes sobre el padrón ya cargado.
  // Permite recuperar filas cuyo error provenía de una regla que cambió
  // después del upload. Solo en borrador.
  const revalidarMutation = useMutation({
    mutationFn: () => revalidarPadron(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['padron', id] });
      queryClient.invalidateQueries({ queryKey: ['asociados', id] });
      queryClient.invalidateQueries({ queryKey: ['inconsistencias', id] });
      queryClient.invalidateQueries({ queryKey: ['padrones'] });
    },
  });

  const early = loadOrErrorView(padron);
  if (early) return early;

  const p = padron.data!;
  const estadosLegajoMap = buildEstadosLegajoMap(
    padronCerrado,
    padronEnValidacion,
    legajos.data,
  );
  const observacionesPorAsociado = groupObservacionesByAsociadoId(legajos.data);
  // Toda la lógica de flags + derivadas vive en un helper para mantener la
  // complejidad cognitiva del componente bajo el umbral de Sonar (S3776).
  const {
    puedeCerrar,
    mostrarEliminarFila,
    mostrarBotonInconsistencias,
    mostrarBotonSeparar,
    mostrarBotonRevalidar,
    mostrarBotonEliminar,
    mostrarBotonDescargarPadron,
    mostrarBotonDescargarEmails,
    mostrarTablaAsociados,
    pendientes,
    mostrarBotonValidarBulk,
    estadoUi,
  } = computePageDerivations({
    padron: p,
    user,
    esAdminMedicus,
    esMedicusVisible,
    puedeMutar,
    padronEnValidacion,
    tab,
    legajosData: legajos.data,
  });

  return (
    <section>
      <header className="pageHeader">
        <h1 className="heading-3">{p.nombreArchivo}</h1>
        <p className="text-element-mid">
          Subido el {new Date(p.fechaSubida).toLocaleString('es-AR')} · Estado:{' '}
          <span className={estadoUi.cls}>{estadoUi.label}</span>
        </p>
        {/* Con qué plan entran los titulares de ESTE padrón y qué promotor queda
            acreditado en Binary. Se congeló al subir: cambiar la organización
            después no mueve a esta gente. */}
        <p className="text-element-mid" style={{ marginTop: 4, fontSize: 13 }}>
          Plan de alta: <strong>{planDeCarga(p, empresas).etiqueta}</strong> · Promotor:{' '}
          <strong style={{ fontFamily: 'monospace' }}>{promotorDeCarga(p, empresas)}</strong>
        </p>
      </header>

      {/* Cifras principales del padrón: prominentes (Total/OK/Errores) y debajo
          los chips chicos del resumen Renaper cuando aplica (admin Medicus). */}
      <div
        style={{
          marginTop: 14,
          display: 'flex',
          flexWrap: 'wrap',
          gap: 10,
          alignItems: 'stretch',
        }}
      >
        <KpiStat label="Total" valor={p.totalRegistros} color="var(--color-primary-darker)" />
        <KpiStat label="OK" valor={p.registrosOk} color="#047857" />
        <KpiStat label="Errores" valor={p.registrosError} color="#B91C1C" />
        {(p.registrosAdvertencia ?? 0) > 0 && (
          <KpiStat
            label="Advertencias"
            valor={p.registrosAdvertencia ?? 0}
            color="#B45309"
            onClick={() => setTab('advertencias')}
          />
        )}
      </div>
      <AdvertenciasBanner
        cantidad={p.registrosAdvertencia ?? 0}
        onVer={() => setTab('advertencias')}
      />
      {esMedicusVisible && renaperResumen.data && renaperResumen.data.total > 0 && (
        <div
          style={{
            marginTop: 10,
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <span style={{ fontSize: 11, color: 'var(--color-element-mid)', marginRight: 4 }}>
            Renaper
          </span>
          <Chip label="Pendientes" valor={renaperResumen.data.pendientes} color="#737373" />
          <Chip label="Validados" valor={renaperResumen.data.validados} color="#16A34A" />
          <Chip label="No coinciden" valor={renaperResumen.data.noCoincide} color="#D97706" />
          <Chip label="No encontrados" valor={renaperResumen.data.noEncontrado} color="#B91C1C" />
          <Chip label="Errores" valor={renaperResumen.data.errores} color="#B91C1C" />
        </div>
      )}

      {esMedicusVisible && renaperLatestJob.data && (
        <RenaperJobBanner
          job={renaperLatestJob.data}
          onDismiss={() => {
            // Cuando el job termino con exito, el banner se autocierra al
            // limpiar el queryCache local. La proxima vez que se haga
            // refetch del job, vuelve a aparecer si el admin re-dispara.
            queryClient.setQueryData(['renaper-latest-job', id], null);
          }}
          onVerPisos={() => setPisosModalOpen(true)}
        />
      )}
      {pisosModalOpen && (
        <PadronPisosModal padronId={id} onClose={() => setPisosModalOpen(false)} />
      )}

      {esMedicusVisible && <HubspotSyncCard padronId={id} padronEstado={p.estado} />}

      {p.estado === 'borrador' && (
        <CorreccionesUploadCard
          uploading={corrPreviewMutation.isPending}
          confirming={corrConfirmMutation.isPending}
          uploadError={corrUploadError}
          result={corrResult}
          onFile={(file) => {
            setCorrUploadError(null);
            setCorrResult(null);
            corrPreviewMutation.mutate(file);
          }}
        />
      )}

      <div className="rowBetween" style={{ marginTop: '1.5rem' }}>
        <div className="tabs">
          {tabsVisibles(p.registrosAdvertencia ?? 0).map((t) => (
            <button
              key={t}
              className={tab === t ? 'tab tab--active' : 'tab'}
              onClick={() => setTab(t)}
            >
              {TAB_LABEL[t]}
            </button>
          ))}
        </div>
        <div className="actions">
          {esMedicusVisible && (
            <Link
              to={`/padrones/${id}/dashboard`}
              className="btn btn-ghost btn--sm"
              style={{ fontWeight: 600 }}
              title="Ver dashboard del padrón: funnel por estado, dónde se quedan los titulares, vencimientos próximos, rechazos."
            >
              Ver dashboard
            </Link>
          )}
          {mostrarBotonInconsistencias && (
            <button
              className="btn btn-ghost btn--sm"
              disabled={descargarInc.isPending}
              onClick={() => descargarInc.mutate()}
              style={{ fontWeight: 600 }}
            >
              {descargarInc.isPending
                ? 'Descargando…'
                : `Descargar inconsistencias (${p.registrosError})`}
            </button>
          )}
          {mostrarBotonSeparar && (
            <button
              className="btn btn-ghost btn--sm"
              disabled={separarMutation.isPending}
              onClick={() => setSepararOpen(true)}
              title="Mueve los legajos con errores a un padrón nuevo y deja este con los OK."
              style={{ fontWeight: 600 }}
            >
              {separarMutation.isPending ? 'Separando…' : 'Separar inconsistencias →'}
            </button>
          )}
          {mostrarBotonDescargarPadron && (
            <button
              className="btn btn-ghost btn--sm"
              onClick={() =>
                setExportar({ tipo: 'padron-completo', titulo: 'Descargar padrón completo' })
              }
              title="Descargar el padrón completo en Excel (todas las filas, sin columna de errores)."
              style={{ fontWeight: 600 }}
            >
              {`Descargar padrón (${p.totalRegistros.toLocaleString('es-AR')})`}
            </button>
          )}
          {mostrarBotonDescargarEmails && (
            <button
              className="btn btn-ghost btn--sm"
              onClick={() =>
                setExportar({ tipo: 'emails-titulares', titulo: 'Descargar emails de titulares' })
              }
              title="Descargar un Excel con los emails de todos los titulares del padrón (una columna `email`, deduplicados). Solo admin Medicus."
              style={{ fontWeight: 600 }}
            >
              Descargar emails titulares
            </button>
          )}
          {mostrarBotonRevalidar && (
            <button
              className="btn btn-ghost btn--sm"
              disabled={revalidarMutation.isPending}
              onClick={() => revalidarMutation.mutate()}
              title="Reaplica las reglas vigentes sobre todas las filas del padrón. Útil cuando se cambió alguna regla después del upload."
              style={{ fontWeight: 600 }}
            >
              {revalidarMutation.isPending ? 'Revalidando…' : 'Revalidar padrón'}
            </button>
          )}
          {p.estado === 'borrador' && (
            <button
              className="btn btn-primary btn--sm"
              disabled={!puedeCerrar || cerrar.isPending}
              onClick={() => cerrar.mutate()}
            >
              <span className="btn-font-style">{labelCerrarBoton(p.estado, cerrar.isPending)}</span>
            </button>
          )}
          {mostrarBotonValidarBulk && (
            <button
              className="btn btn-primary btn--sm"
              disabled={validarPadronMut.isPending}
              onClick={() => validarPadronMut.mutate()}
              title={
                pendientes.length > 0
                  ? 'Valida todos los legajos pendientes del padrón de una sola vez.'
                  : 'Finaliza el padrón. No quedan legajos por validar; si hay integrantes a sumar se resuelven aparte desde Alta Integrantes.'
              }
            >
              <span className="btn-font-style">
                {validarPadronMut.isPending
                  ? 'Validando…'
                  : pendientes.length > 0
                    ? `Validar padrón completo (${pendientes.length} pendientes)`
                    : 'Cerrar padrón'}
              </span>
            </button>
          )}
          {esAdminMedicus && puedeMutar && p.registrosOk > 0 && (
            <RenaperBatchButton
              resumen={renaperResumen.data}
              enCurso={renaperEnCurso}
              disparando={validarRenaperPadronMut.isPending}
              onDisparar={() => validarRenaperPadronMut.mutate()}
            />
          )}
          {mostrarBotonEliminar && (
            <button
              className="btn btn-ghost btn--sm"
              onClick={() => setEliminarOpen(true)}
              style={{ color: 'var(--color-error-dark)', borderColor: 'var(--color-error-light)' }}
              title="Eliminar padrón y todos sus datos asociados"
            >
              Eliminar padrón
            </button>
          )}
        </div>
      </div>

      {validarPadronMut.isError && (
        <p className="status status-error" style={{ marginTop: '0.5rem', display: 'block' }}>
          {extractServerError(validarPadronMut.error)}
        </p>
      )}
      <AvisoValidacionSinWizard resultado={validarPadronMut.data} />
      {/* El resto de las acciones de la fila de botones fallaba en silencio. */}
      <ErrorInline
        error={descargarInc.error}
        fallback="No se pudieron descargar las inconsistencias."
        style={{ marginTop: '0.5rem' }}
      />
      <ErrorInline
        error={previewCierreMut.error ?? cerrarMut.error}
        fallback="No se pudo cerrar el padrón."
        style={{ marginTop: '0.5rem' }}
      />
      <ErrorInline
        error={revalidarMutation.error}
        fallback="No se pudo revalidar el padrón."
        style={{ marginTop: '0.5rem' }}
      />
      <ErrorInline
        error={validarLegajoMut.error}
        fallback="No se pudo validar el legajo."
        style={{ marginTop: '0.5rem' }}
      />
      <ErrorInline
        error={validarRenaperPadronMut.error}
        fallback="No se pudo lanzar la validación contra Renaper."
        style={{ marginTop: '0.5rem' }}
      />

      {mostrarTablaAsociados && (
        <div className="card" style={{ marginTop: '1rem' }}>
          <div className="rowBetween" style={{ marginBottom: '0.75rem' }}>
            <input
              className="input"
              style={{ maxWidth: 360 }}
              placeholder="Buscar por apellido, CUIL, documento, email…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <p
              className="text-element-mid"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
            >
              {asociados.isFetching && asociados.data && <Spinner size={12} />}
              {asociados.data?.total ?? 0} registros
            </p>
          </div>
          {/* Banda animada arriba de la tabla mientras hay refetch en background
              (search, paginación, invalidación tras edit). No tapa la data
              vieja para que el operador no pierda el contexto visual. */}
          <RefetchBar visible={!!asociados.data && asociados.isFetching} />
          {asociados.isLoading && !asociados.data && <TableSkeleton rows={10} />}
          {asociados.isError && !asociados.data && (
            <p className="status status-error" style={{ display: 'block' }}>
              No se pudieron cargar los registros. {extractServerError(asociados.error)}
            </p>
          )}
          {asociados.data && (
            <AsociadosTable
              asociados={asociados.data.items}
              // UE: solo puede editar/borrar mientras el padron este en
              // borrador. Una vez cerrado (pendiente_validacion o cerrado),
              // sus permisos de mutacion quedan bloqueados — coincide con
              // el guard del server en AsociadosController.assertAccess.
              // Admin Medicus mantiene la posibilidad de editar siempre.
              // Borrar: en borrador, o en pendiente de validación con la clave
              // `view:padron-quitar-fila` (ver `mostrarEliminarFila`).
              onEdit={
                puedeMutar && (esAdminMedicus || p.estado === 'borrador')
                  ? (a) => setEditing(a)
                  : undefined
              }
              onDelete={
                mostrarEliminarFila
                  ? (a) =>
                      confirmarYEliminarAsociado(
                        a,
                        eliminarAsociadoMut.mutate,
                        p.estado !== 'borrador',
                      )
                  : undefined
              }
              estadosLegajo={estadosLegajoMap}
              observacionesPorAsociado={observacionesPorAsociado}
              // Renaper es info interna de Medicus — UE no debe verla.
              // USER_MEDICUS sí ve la info (solo lectura).
              mostrarRenaper={esMedicusVisible}
            />
          )}
        </div>
      )}

      {tab === 'legajos' && (
        <LegajosTabContent
          padronEstado={p.estado}
          padronEnValidacion={padronEnValidacion}
          padronCerrado={padronCerrado}
          esAdminMedicus={esAdminMedicus && puedeMutar}
          legajos={legajos.data}
          resumen={resumenLegajos.data}
          validarLegajoMut={validarLegajoMut}
          onRechazarLegajo={(l) => {
            setRechazarTarget(l);
            setMotivoRechazo('');
          }}
          onEliminarLegajo={(l) =>
            elim.abrir({
              id: l.id,
              numero: l.numero,
              familiares: l.familiaresAsociadoIds.length,
            })
          }
        />
      )}

      <PadronDetalleModals
        editing={editing}
        setEditing={setEditing}
        update={update}
        corrPreview={corrPreview}
        setCorrPreview={setCorrPreview}
        setCorrConfirmError={setCorrConfirmError}
        corrConfirmError={corrConfirmError}
        corrConfirmMutation={corrConfirmMutation}
        eliminarOpen={eliminarOpen}
        setEliminarOpen={setEliminarOpen}
        padronNombre={p.nombreArchivo}
        padronEstado={p.estado}
        eliminarMutation={eliminarMutation}
        elim={elim}
        rechazarTarget={rechazarTarget}
        setRechazarTarget={setRechazarTarget}
        motivoRechazo={motivoRechazo}
        setMotivoRechazo={setMotivoRechazo}
        rechazarLegajoMut={rechazarLegajoMut}
        separarOpen={separarOpen}
        setSepararOpen={setSepararOpen}
        separarMutation={separarMutation}
        padronRegistrosError={p.registrosError}
        padronRegistrosOk={p.registrosOk}
      />
      {previewCierre && (
        <ConfirmarCierrePadronModal
          preview={previewCierre}
          confirmando={cerrarMut.isPending}
          onCancelar={() => setPreviewCierre(null)}
          onConfirmar={() => cerrarMut.mutate({ confirmarMerges: true })}
        />
      )}
      {exportar && (
        <ExportModal
          tipo={exportar.tipo}
          params={{ padronId: id }}
          titulo={exportar.titulo}
          onClose={() => setExportar(null)}
        />
      )}
    </section>
  );
}

/**
 * Modal previo al cierre del padrón. Aparece si el preview detectó:
 *   - merges: legajos del padrón nuevo cuyo número coincide con un legajo
 *     existente en estado mergeable; las personas se SUMAN al legajo
 *     existente (que ya no se vuelve a crear).
 *   - bis: legajos del padrón nuevo cuyo número coincide con un legajo en
 *     flujo avanzado; el nuevo queda en estado "Adherentes" para
 *     procesamiento aparte (no requieren confirmación, se informan).
 *
 * Si NO hay ni merges ni bis, este modal no se muestra — `cerrar.mutate()`
 * pasa directo al endpoint con un body vacío.
 */
/**
 * Tras validar un padrón de una organización SIN asistente del titular: que
 * quede claro que NO salió ningún mail y que los legajos ya están en la cola
 * del alta. Para las demás organizaciones no muestra nada.
 */
function AvisoValidacionSinWizard({ resultado }: { resultado: ValidarPadronResult | undefined }) {
  if (!resultado?.sinWizard) return null;
  const n = resultado.legajosValidados;
  const bis = resultado.bisActivados ?? 0;
  const legajos = `${n} ${n === 1 ? 'legajo quedó' : 'legajos quedaron'} directo en «Listo para alta»`;
  const bises = bis > 0 ? ` y ${bis} bis ${bis === 1 ? 'quedó activado' : 'quedaron activados'}` : '';
  return (
    <p className="status status-info" style={{ marginTop: '0.5rem', display: 'block' }}>
      Organización sin asistente del titular: {legajos}
      {bises}, sin invitaciones por mail. El despacho a Binary los toma en la próxima corrida
      automática o desde Alta Grupos.
    </p>
  );
}

function ConfirmarCierrePadronModal(props: {
  preview: PreviewCierre;
  confirmando: boolean;
  onCancelar: () => void;
  onConfirmar: () => void;
}) {
  const { preview, confirmando, onCancelar, onConfirmar } = props;
  const { totales, merges, bis } = preview;
  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={confirmando ? undefined : onCancelar}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15, 23, 42, 0.55)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 100,
        padding: 16,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="card"
        style={{
          maxWidth: 640,
          width: '100%',
          maxHeight: '85vh',
          overflow: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        <h2 className="heading-5" style={{ margin: 0 }}>
          Confirmar cierre del padrón
        </h2>
        <p className="text-element-mid" style={{ fontSize: 13, margin: 0 }}>
          Detectamos números de legajo que ya existen en otros padrones. Revisá los
          casos antes de confirmar.
        </p>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
            gap: 8,
          }}
        >
          <KpiCelda label="Nuevos" valor={totales.legajosACrear} tone="info" />
          <KpiCelda label="A mergear" valor={totales.legajosAMergear} tone="warning" />
          <KpiCelda label="Adherentes (bis)" valor={totales.legajosBis} tone="error" />
        </div>

        {merges.length > 0 && (
          <section>
            <h3 style={{ margin: '4px 0 6px', fontSize: 13 }}>
              Merges ({merges.length}) — {totales.personasAMergear} persona
              {totales.personasAMergear === 1 ? '' : 's'} se van a sumar a legajos existentes
            </h3>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, maxHeight: 160, overflow: 'auto' }}>
              {merges.map((m) => (
                <li key={m.numero} style={{ marginBottom: 4 }}>
                  Legajo <strong>{m.numero}</strong>: <strong>{m.personas}</strong>{' '}
                  persona{m.personas === 1 ? '' : 's'} se suman al legajo existente (estado{' '}
                  <em>{m.estadoExistente}</em>)
                </li>
              ))}
            </ul>
          </section>
        )}

        {bis.length > 0 && (
          <section>
            <h3 style={{ margin: '4px 0 6px', fontSize: 13 }}>
              Adherentes ({bis.length}) — quedan a procesar por la modalidad aparte
            </h3>
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, maxHeight: 160, overflow: 'auto' }}>
              {bis.map((b) => (
                <li key={b.numero} style={{ marginBottom: 4 }}>
                  Legajo <strong>{b.numero}</strong>: <strong>{b.personas}</strong> persona
                  {b.personas === 1 ? '' : 's'}, el original está en{' '}
                  <em>{b.estadoExistente}</em>
                </li>
              ))}
            </ul>
          </section>
        )}

        <footer style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 8 }}>
          <button
            type="button"
            className="btn btn-ghost"
            onClick={onCancelar}
            disabled={confirmando}
          >
            Cancelar
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={onConfirmar}
            disabled={confirmando}
          >
            {confirmando ? 'Cerrando…' : 'Confirmar y cerrar'}
          </button>
        </footer>
      </div>
    </div>
  );
}

function KpiCelda({
  label,
  valor,
  tone,
}: {
  label: string;
  valor: number;
  tone: 'info' | 'warning' | 'error';
}) {
  const colors = {
    info: { bg: '#EFF6FF', border: '#BFDBFE', color: '#1E40AF' },
    warning: { bg: '#FEF3C7', border: '#FCD34D', color: '#92400E' },
    error: { bg: '#FEF2F2', border: '#FECACA', color: '#991B1B' },
  }[tone];
  return (
    <div
      style={{
        padding: '8px 10px',
        background: colors.bg,
        border: `1px solid ${colors.border}`,
        borderRadius: 6,
      }}
    >
      <p style={{ fontSize: 11, margin: 0, color: 'var(--color-element-mid)' }}>{label}</p>
      <strong style={{ fontSize: 22, color: colors.color, fontVariantNumeric: 'tabular-nums' }}>
        {valor.toLocaleString('es-AR')}
      </strong>
    </div>
  );
}

/**
 * Acción secundaria — corregir asociados re-subiendo el archivo. Colapsado
 * por default para que no domine la página (el flujo principal es leer la
 * tabla de errores y editar inline). Se auto-expande si hay upload activo,
 * error o resultado para que el feedback nunca quede oculto.
 */
function CorreccionesUploadCard({
  uploading,
  confirming,
  uploadError,
  result,
  onFile,
}: {
  uploading: boolean;
  confirming: boolean;
  uploadError: string | null;
  result: CorreccionesResult | null;
  onFile: (file: File) => void;
}) {
  const [open, setOpen] = useState(false);
  const expanded = open || uploading || confirming || !!uploadError || !!result;
  return (
    <div
      className="card"
      style={{
        marginTop: '0.75rem',
        padding: expanded ? '12px 14px' : '8px 12px',
      }}
    >
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <strong style={{ fontSize: 13 }}>Subir archivo de correcciones</strong>
          <span
            className="text-element-mid"
            style={{ fontSize: 12 }}
            title="Mismo formato que la carga original. Match por DOCUMENTO. Filas OK se omiten; nuevas se agregan."
          >
            (match por DOCUMENTO)
          </span>
        </div>
        {!expanded && (
          <button
            type="button"
            className="btn btn-ghost"
            style={{ fontSize: 12, padding: '4px 10px' }}
            onClick={() => setOpen(true)}
          >
            + Subir
          </button>
        )}
        {expanded && open && !uploading && !confirming && (
          <button
            type="button"
            className="btn btn-link"
            style={{ fontSize: 12 }}
            onClick={() => setOpen(false)}
          >
            Ocultar
          </button>
        )}
      </div>
      {expanded && (
        <div style={{ marginTop: 10 }}>
          <UploadDropzone disabled={uploading || confirming} onFile={onFile} />
          {uploading && (
            <p className="status status-info" style={{ marginTop: 8 }}>
              Leyendo archivo de correcciones…
            </p>
          )}
          {uploadError && (
            <p className="status status-error" style={{ marginTop: 8 }}>
              {uploadError}
            </p>
          )}
          {result && (
            <p
              className="status status-success"
              style={{ marginTop: 8, display: 'block' }}
            >
              Correcciones aplicadas — actualizadas: <strong>{result.actualizadas}</strong>{' '}
              · agregadas: <strong>{result.agregadas}</strong> · omitidas:{' '}
              <strong>{result.omitidas}</strong>
            </p>
          )}
        </div>
      )}
    </div>
  );
}

interface LegajosTabContentProps {
  padronEstado: string;
  padronEnValidacion: boolean;
  padronCerrado: boolean;
  esAdminMedicus: boolean;
  legajos: Legajo[] | undefined;
  resumen: { totalLegajos: number; porEstado: Record<string, number> } | undefined;
  validarLegajoMut: {
    isPending: boolean;
    variables: string | undefined;
    mutate: (id: string) => void;
  };
  onRechazarLegajo: (l: Legajo) => void;
  onEliminarLegajo: (l: Legajo) => void;
}

function LegajosTabContent(props: LegajosTabContentProps) {
  const { padronEstado, padronEnValidacion, padronCerrado, legajos, resumen } = props;
  const hayLegajos = !!legajos && legajos.length > 0;
  const padronProcesado = padronEnValidacion || padronCerrado;
  const sinLegajos = padronEstado !== 'borrador' && resumen?.totalLegajos === 0;
  return (
    <div style={{ marginTop: '1rem', display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {padronEstado === 'borrador' && (
        <InfoAlert icon={AlertCircle} tone="warning">
          Cerrá el padrón para generar los legajos. Cada legajo arranca{' '}
          <strong>pendiente de validación</strong> por un admin Medicus.
        </InfoAlert>
      )}
      {padronProcesado && resumen && <LegajosResumenCards resumen={resumen} />}
      {hayLegajos && <LegajosTable {...props} legajos={legajos!} />}
      {sinLegajos && (
        <InfoAlert icon={Info}>
          Este padrón todavía no tiene legajos persistidos.
        </InfoAlert>
      )}
    </div>
  );
}

function LegajosResumenCards({
  resumen,
}: {
  resumen: { totalLegajos: number; porEstado: Record<string, number> };
}) {
  return (
    <div className="grid grid-cols-3 gap-md">
      <article className="card">
        <p className="text-element-mid">Total legajos</p>
        <p className="heading-3 text-primary">{resumen.totalLegajos}</p>
      </article>
      <article className="card">
        <p className="text-element-mid">Pendientes de validación</p>
        <p className="heading-3 text-warning">
          {resumen.porEstado.pendiente_validacion_medicus ?? 0}
        </p>
      </article>
      <article className="card">
        <p className="text-element-mid">Rechazados por Medicus</p>
        <p className="heading-3 text-error">{resumen.porEstado.rechazado_medicus ?? 0}</p>
      </article>
    </div>
  );
}

function LegajosTable({
  esAdminMedicus,
  legajos,
  validarLegajoMut,
  onRechazarLegajo,
  onEliminarLegajo,
}: Omit<LegajosTabContentProps, 'legajos' | 'resumen' | 'padronEstado' | 'padronEnValidacion' | 'padronCerrado'> & {
  legajos: Legajo[];
}) {
  const visibles = legajos.slice(0, 100);
  return (
    <div className="card">
      <h2 className="heading-5">Legajos ({legajos.length})</h2>
      <div className="tableWrap">
        <table className="dataTable">
          <thead>
            <tr>
              <th>Legajo</th>
              <th>Familiares</th>
              <th>Estado</th>
              <th>Vence</th>
              <th style={{ minWidth: 220 }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {visibles.map((l) => (
              <LegajoRow
                key={l.id}
                legajo={l}
                esAdminMedicus={esAdminMedicus}
                validarLegajoMut={validarLegajoMut}
                onRechazar={onRechazarLegajo}
                onEliminar={onEliminarLegajo}
              />
            ))}
          </tbody>
        </table>
      </div>
      {legajos.length > 100 && (
        <p className="text-element-mid" style={{ marginTop: '0.5rem', fontSize: 12 }}>
          Mostrando los primeros 100 de {legajos.length}.
        </p>
      )}
    </div>
  );
}

function LegajoRow({
  legajo: l,
  esAdminMedicus,
  validarLegajoMut,
  onRechazar,
  onEliminar,
}: {
  legajo: Legajo;
  esAdminMedicus: boolean;
  validarLegajoMut: LegajosTabContentProps['validarLegajoMut'];
  onRechazar: (l: Legajo) => void;
  onEliminar: (l: Legajo) => void;
}) {
  const bloqueado = l.estado === 'en_alta_binary' || l.estado === 'dado_de_alta';
  const enPendiente = l.estado === 'pendiente_validacion_medicus';
  const validandoEste = validarLegajoMut.isPending && validarLegajoMut.variables === l.id;
  const colorEliminar = bloqueado ? 'var(--color-element-mid)' : 'var(--color-error-dark)';
  const tituloEliminar = bloqueado
    ? 'Este legajo ya tiene alta en Binary, no se puede eliminar desde el portal.'
    : 'Eliminar legajo (cascade)';
  return (
    <tr>
      <td>
        <strong>{l.numero}</strong>
      </td>
      <td>{l.familiaresAsociadoIds.length}</td>
      <td>
        <EstadoLegajoBadge estado={l.estado} />
        {l.validadoSinWizard && (
          // «Listo para alta» dice "el titular terminó el onboarding"; acá no
          // hubo onboarding: la organización no usa el asistente.
          <div
            className="text-element-mid"
            style={{ fontSize: 11, marginTop: 2 }}
            title={`Validado sin asistente del titular el ${new Date(
              l.validadoSinWizard.at,
            ).toLocaleString('es-AR')} por ${l.validadoSinWizard.por}`}
          >
            sin asistente del titular
          </div>
        )}
        {l.estado === 'rechazado_medicus' && l.validacionMedicus?.motivo && (
          <div style={{ fontSize: 11, color: 'var(--color-error)', marginTop: 2 }}>
            Motivo: {l.validacionMedicus.motivo}
          </div>
        )}
      </td>
      <td>{new Date(l.activacionExpiraAt).toLocaleDateString('es-AR')}</td>
      <td>
        <div style={{ display: 'flex', gap: 6 }}>
          {esAdminMedicus && enPendiente && (
            <>
              <button
                className="btn btn-link"
                disabled={validarLegajoMut.isPending}
                onClick={() => validarLegajoMut.mutate(l.id)}
                style={{ color: 'var(--color-success-dark)' }}
              >
                {validandoEste ? 'Validando…' : 'Validar'}
              </button>
              <button
                className="btn btn-link"
                onClick={() => onRechazar(l)}
                style={{ color: 'var(--color-error-dark)' }}
              >
                Rechazar
              </button>
            </>
          )}
          <button
            className="btn btn-link"
            disabled={bloqueado}
            onClick={() => onEliminar(l)}
            title={tituloEliminar}
            style={{ color: colorEliminar }}
          >
            Eliminar
          </button>
        </div>
      </td>
    </tr>
  );
}

interface PadronDetalleModalsProps {
  editing: Asociado | null;
  setEditing: (a: Asociado | null) => void;
  update: { mutateAsync: (v: { aId: string; patch: Partial<Asociado> }) => Promise<unknown> };
  corrPreview: PreviewResponse | null;
  setCorrPreview: (p: PreviewResponse | null) => void;
  setCorrConfirmError: (e: string | null) => void;
  corrConfirmError: string | null;
  corrConfirmMutation: {
    isPending: boolean;
    mutate: (m: Record<string, number>) => void;
  };
  eliminarOpen: boolean;
  setEliminarOpen: (o: boolean) => void;
  padronNombre: string;
  padronEstado: 'borrador' | 'pendiente_validacion' | 'cerrado';
  eliminarMutation: {
    isPending: boolean;
    error: unknown;
    mutate: () => void;
  };
  elim: ReturnType<typeof useEliminarLegajo>;
  rechazarTarget: Legajo | null;
  setRechazarTarget: (l: Legajo | null) => void;
  motivoRechazo: string;
  setMotivoRechazo: (m: string) => void;
  rechazarLegajoMut: {
    isPending: boolean;
    error: unknown;
    mutate: (v: { legajoId: string; motivo: string }) => void;
  };
  separarOpen: boolean;
  setSepararOpen: (o: boolean) => void;
  separarMutation: {
    isPending: boolean;
    error: unknown;
    mutate: () => void;
  };
  padronRegistrosError: number;
  padronRegistrosOk: number;
}

function PadronDetalleModals(props: PadronDetalleModalsProps) {
  const {
    editing,
    setEditing,
    update,
    corrPreview,
    setCorrPreview,
    setCorrConfirmError,
    corrConfirmError,
    corrConfirmMutation,
    eliminarOpen,
    setEliminarOpen,
    padronNombre,
    padronEstado,
    eliminarMutation,
    elim,
    rechazarTarget,
    setRechazarTarget,
    motivoRechazo,
    setMotivoRechazo,
    rechazarLegajoMut,
    separarOpen,
    setSepararOpen,
    separarMutation,
    padronRegistrosError,
    padronRegistrosOk,
  } = props;
  return (
    <>
      {editing && (
        <EditAsociadoModal
          asociado={editing}
          onClose={() => setEditing(null)}
          onSave={async (patch) => {
            await update.mutateAsync({ aId: editing.id, patch });
          }}
        />
      )}
      {corrPreview && (
        <ColumnMappingModal
          preview={corrPreview}
          submitting={corrConfirmMutation.isPending}
          serverError={corrConfirmError}
          onCancel={() => {
            setCorrPreview(null);
            setCorrConfirmError(null);
          }}
          onConfirm={(mapping) => corrConfirmMutation.mutate(mapping)}
        />
      )}
      {eliminarOpen && (
        <EliminarPadronModal
          nombre={padronNombre}
          estado={padronEstado}
          submitting={eliminarMutation.isPending}
          error={eliminarMutation.error ? extractServerError(eliminarMutation.error) : null}
          onCancel={() => setEliminarOpen(false)}
          onConfirm={() => eliminarMutation.mutate()}
        />
      )}
      {separarOpen && (
        <SepararInconsistenciasModal
          nombre={padronNombre}
          registrosError={padronRegistrosError}
          registrosOk={padronRegistrosOk}
          submitting={separarMutation.isPending}
          error={separarMutation.error ? extractServerError(separarMutation.error) : null}
          onCancel={() => setSepararOpen(false)}
          onConfirm={() => separarMutation.mutate()}
        />
      )}
      {elim.target && (
        <EliminarLegajoModal
          target={elim.target}
          confirmText={elim.confirmText}
          setConfirmText={elim.setConfirmText}
          error={elim.error}
          submitting={elim.submitting}
          puedeConfirmar={elim.puedeConfirmar}
          onCancel={elim.cancelar}
          onConfirm={elim.confirmar}
        />
      )}
      {rechazarTarget && (
        <RechazarLegajoModal
          legajo={rechazarTarget}
          motivo={motivoRechazo}
          setMotivo={setMotivoRechazo}
          submitting={rechazarLegajoMut.isPending}
          error={rechazarLegajoMut.error ? extractServerError(rechazarLegajoMut.error) : null}
          onCancel={() => {
            setRechazarTarget(null);
            setMotivoRechazo('');
          }}
          onConfirm={() =>
            rechazarLegajoMut.mutate({ legajoId: rechazarTarget.id, motivo: motivoRechazo })
          }
        />
      )}
    </>
  );
}

function RechazarLegajoModal({
  legajo,
  motivo,
  setMotivo,
  submitting,
  error,
  onCancel,
  onConfirm,
}: {
  legajo: Legajo;
  motivo: string;
  setMotivo: (m: string) => void;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const motivoOk = motivo.trim().length > 0 && motivo.trim().length <= 500;
  return (
    <div className="modal__overlay">
      <div className="modal__content" style={{ maxWidth: 480 }}>
        <h2 className="heading-5" style={{ marginBottom: 8, color: 'var(--color-error-dark)' }}>
          Rechazar legajo {legajo.numero}
        </h2>
        <p className="text-element-mid" style={{ fontSize: 14, marginBottom: 12 }}>
          El motivo se le muestra al titular cuando intenta entrar al portal.
          Sé claro para que sepa qué corregir desde RRHH.
        </p>
        <label className="formField" style={{ display: 'block' }}>
          <span style={{ display: 'block', marginBottom: 6, fontSize: 13 }}>
            Motivo del rechazo
          </span>
          <textarea
            className="input"
            rows={4}
            maxLength={500}
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
            placeholder="Ej. El DNI no corresponde al titular registrado en Binary."
          />
          <span className="onboarding__fieldHint">{motivo.length}/500</span>
        </label>
        {error && (
          <p className="status status-error" style={{ marginTop: 8, display: 'block' }}>
            {error}
          </p>
        )}
        <div className="modal__actions" style={{ marginTop: 12 }}>
          <button className="btn btn-ghost" onClick={onCancel} disabled={submitting}>
            Cancelar
          </button>
          <button
            className="btn btn-primary"
            onClick={onConfirm}
            disabled={!motivoOk || submitting}
            style={{ background: 'var(--color-error-dark)' }}
          >
            {submitting ? 'Rechazando…' : 'Rechazar legajo'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Tile chico (label arriba + número grande abajo) para los KPIs principales
 * del padrón: Total, OK, Errores. Pensado para que se lean de un vistazo
 * sin tener que entornar los ojos. Usa una barra superior del mismo color
 * que el número para distinguir los tres rápido.
 */
function KpiStat({
  label,
  valor,
  color,
  onClick,
}: {
  label: string;
  valor: number;
  color: string;
  /** Si viene, el tile es un botón que lleva a ver el detalle de ese número. */
  onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      type={onClick ? 'button' : undefined}
      onClick={onClick}
      title={onClick ? `Ver ${label.toLowerCase()}` : undefined}
      style={{
        flex: '0 1 160px',
        minWidth: 130,
        padding: '12px 16px',
        background: 'var(--color-surface)',
        border: '1px solid var(--color-outline)',
        borderTop: `3px solid ${color}`,
        borderRadius: 10,
        display: 'flex',
        flexDirection: 'column',
        gap: 2,
        boxShadow: '0 1px 2px rgba(0,0,0,0.03)',
        textAlign: 'left',
        font: 'inherit',
        cursor: onClick ? 'pointer' : 'default',
      }}
    >
      <span
        style={{
          fontSize: 11,
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
          color: 'var(--color-element-mid)',
          fontWeight: 600,
        }}
      >
        {label}
      </span>
      <strong style={{ color, fontSize: 26, lineHeight: 1.1, fontVariantNumeric: 'tabular-nums' }}>
        {valor.toLocaleString('es-AR')}
      </strong>
    </Tag>
  );
}

/** Chip compacto para resúmenes secundarios (Renaper, etc.). */
function Chip({
  label,
  valor,
  color,
}: {
  label: string;
  valor: number;
  color: string;
}) {
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 999,
        background: 'var(--color-surface-alt)',
        border: '1px solid var(--color-outline)',
        fontSize: 12,
        whiteSpace: 'nowrap',
      }}
    >
      <span style={{ color: 'var(--color-element-mid)' }}>{label}</span>
      <strong style={{ color, fontVariantNumeric: 'tabular-nums' }}>
        {valor.toLocaleString('es-AR')}
      </strong>
    </span>
  );
}

/**
 * Banner del estado del último job de validación masiva contra Renaper.
 *
 * - `corriendo`: indicador animado + progreso en vivo (validados/candidatos)
 *   leído del resumen actual del padrón. No tiene botón cerrar — mientras
 *   corre el polling lo vuelve a traer de Mongo igual.
 * - `terminado`: muestra el resumen del cierre + botón cerrar.
 * - `error`: muestra el mensaje + botón cerrar; el admin puede reintentar
 *   con el botón "Validar Renaper" de arriba.
 */
/**
 * Botón que dispara la validación batch contra Renaper. Vive afuera del
 * cuerpo de PadronDetallePage para mantener su complejidad cognitiva bajo
 * el umbral de Sonar S3776 (los ternarios anidados del label/title/style
 * cuentan en la función contenedora).
 */
function RenaperBatchButton({
  resumen,
  enCurso,
  disparando,
  onDisparar,
}: {
  resumen: import('@lib/services/renaper.service').ResumenRenaperPadron | undefined;
  enCurso: boolean;
  disparando: boolean;
  onDisparar: () => void;
}) {
  const todosValidados =
    !!resumen && resumen.total > 0 && resumen.validados === resumen.total;
  const pendientesCount = resumen ? resumen.total - resumen.validados : 0;
  const labelDescanso = labelBoton(todosValidados, pendientesCount, resumen);
  // El botón queda deshabilitado mientras el job corre — el batch sigue solo,
  // no hay que apretar de nuevo. Si vuelve la pantalla post-restart o el admin
  // cierra y reabre el tab, el banner se reconstruye desde `renaperLatestJob`
  // (poll inicial al montar).
  const deshabilitado = disparando || enCurso || todosValidados;
  const label = enCurso ? 'Validación en curso…' : disparando ? 'Disparando…' : labelDescanso;
  const title = tituloBoton(enCurso, todosValidados);
  return (
    <button
      className="btn btn-primary btn--sm"
      disabled={deshabilitado}
      onClick={onDisparar}
      title={title}
      style={{
        // Color distinto del btn-primary navy ("Validar padrón completo").
        // Cuando está completo, usamos verde success para señalar OK.
        backgroundColor: todosValidados
          ? 'var(--color-success-dark)'
          : 'var(--color-info-dark)',
        boxShadow: todosValidados
          ? '0 1px 2px rgba(29, 120, 75, 0.25)'
          : '0 1px 2px rgba(30, 58, 138, 0.25)',
      }}
    >
      <span className="btn-font-style">{label}</span>
    </button>
  );
}

function labelBoton(
  todosValidados: boolean,
  pendientesCount: number,
  resumen: { total: number; validados: number } | undefined,
): string {
  if (todosValidados && resumen) return `Renaper completo (${resumen.validados}/${resumen.total})`;
  if (pendientesCount > 0) return `Validar Renaper (${pendientesCount} pendientes)`;
  return 'Validar Renaper';
}

function tituloBoton(enCurso: boolean, todosValidados: boolean): string {
  if (enCurso) return 'La validación está corriendo en segundo plano. Te avisamos cuando termine.';
  if (todosValidados) return 'Todos los asociados ya están validados contra Renaper.';
  return 'Valida contra Renaper. Los ya validados se saltean automáticamente.';
}

function RenaperJobBanner({
  job,
  onDismiss,
  onVerPisos,
}: {
  job: import('@lib/services/renaper.service').RenaperBatchJob;
  onDismiss: () => void;
  onVerPisos: () => void;
}) {
  const startedAt = new Date(job.startedAt);
  const horaInicio = startedAt.toLocaleTimeString('es-AR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  if (job.estado === 'corriendo') {
    const candidatos = job.candidatos ?? 0;
    const procesadosClamp = Math.max(0, Math.min(candidatos, job.procesados ?? 0));
    const pct = candidatos > 0 ? Math.round((procesadosClamp / candidatos) * 100) : 0;
    return (
      <div
        role="status"
        aria-live="polite"
        style={{
          marginTop: 12,
          padding: '12px 14px',
          background: '#EFF6FF',
          border: '1px solid #BFDBFE',
          borderRadius: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <Spinner size={14} />
          <strong style={{ color: '#1E3A8A', fontSize: 14 }}>
            Validando contra Renaper en segundo plano
          </strong>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--color-element-mid)' }}>
            Iniciada a las {horaInicio}
          </span>
        </div>
        <div style={{ fontSize: 13, color: 'var(--color-element-mid)' }}>
          {candidatos > 0 ? (
            <>
              Procesados <strong>{procesadosClamp.toLocaleString('es-AR')}</strong> de{' '}
              <strong>{candidatos.toLocaleString('es-AR')}</strong> ({pct}%). Podés cerrar
              esta pantalla y volver más tarde — al volver te mostramos el resultado.
            </>
          ) : (
            <>Preparando el lote… enseguida vas a ver el progreso en vivo.</>
          )}
        </div>
        {candidatos > 0 && (
          <div
            style={{
              height: 6,
              background: '#DBEAFE',
              borderRadius: 999,
              overflow: 'hidden',
            }}
          >
            <div
              style={{
                width: `${pct}%`,
                height: '100%',
                background: '#1D4ED8',
                transition: 'width 0.4s ease',
              }}
            />
          </div>
        )}
      </div>
    );
  }

  if (job.estado === 'terminado') {
    const r = job.resumen;
    const finishedAt = job.finishedAt ? new Date(job.finishedAt) : null;
    const duracionSeg = finishedAt
      ? Math.round((finishedAt.getTime() - startedAt.getTime()) / 1000)
      : 0;
    const duracionFmt =
      duracionSeg < 60 ? `${duracionSeg}s` : `${Math.floor(duracionSeg / 60)} min ${duracionSeg % 60}s`;
    return (
      <div
        role="status"
        style={{
          marginTop: 12,
          padding: '12px 14px',
          background: '#F0FDF4',
          border: '1px solid #BBF7D0',
          borderRadius: 10,
          display: 'flex',
          alignItems: 'flex-start',
          gap: 12,
        }}
      >
        <div style={{ flex: 1 }}>
          <strong style={{ color: '#166534', fontSize: 14 }}>
            Validación contra Renaper completada
          </strong>
          <p style={{ marginTop: 4, fontSize: 13, color: 'var(--color-element-mid)' }}>
            {r ? (
              <>
                <strong>{r.validados.toLocaleString('es-AR')}</strong> validados ·{' '}
                <strong>{r.noCoincide.toLocaleString('es-AR')}</strong> no coinciden ·{' '}
                <strong>{r.noEncontrado.toLocaleString('es-AR')}</strong> no encontrados ·{' '}
                <strong>{r.errores.toLocaleString('es-AR')}</strong> con error. Tardó{' '}
                {duracionFmt}.
              </>
            ) : (
              <>Lote terminado en {duracionFmt}.</>
            )}
          </p>
          {r && r.pisosAplicados !== undefined && r.pisosAplicados > 0 && (
            <p
              style={{
                marginTop: 6,
                fontSize: 13,
                color: '#166534',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                flexWrap: 'wrap',
              }}
            >
              <span>
                <strong>{r.pisosAplicados.toLocaleString('es-AR')}</strong> legajos pasaron a{' '}
                <strong>validado</strong> tras pisar campos con datos de Renaper.
              </span>
              <button
                type="button"
                onClick={onVerPisos}
                className="btn btn-ghost btn--sm"
                style={{
                  background: 'white',
                  border: '1px solid #BBF7D0',
                  color: '#166534',
                  fontWeight: 700,
                  padding: '4px 10px',
                }}
              >
                Ver detalle de pisos
              </button>
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Cerrar"
          style={{
            background: 'none',
            border: 'none',
            cursor: 'pointer',
            fontSize: 18,
            color: '#166534',
            lineHeight: 1,
            padding: 0,
          }}
        >
          ×
        </button>
      </div>
    );
  }

  // estado === 'error'
  return (
    <div
      role="alert"
      style={{
        marginTop: 12,
        padding: '12px 14px',
        background: '#FEF2F2',
        border: '1px solid #FECACA',
        borderRadius: 10,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
      }}
    >
      <div style={{ flex: 1 }}>
        <strong style={{ color: '#991B1B', fontSize: 14 }}>
          La validación contra Renaper falló
        </strong>
        <p style={{ marginTop: 4, fontSize: 13, color: 'var(--color-element-mid)' }}>
          {job.error ?? 'Error desconocido.'} Podés reintentar con el botón “Validar Renaper”.
        </p>
      </div>
      <button
        type="button"
        onClick={onDismiss}
        aria-label="Cerrar"
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          fontSize: 18,
          color: '#991B1B',
          lineHeight: 1,
          padding: 0,
        }}
      >
        ×
      </button>
    </div>
  );
}
